import { useTransition } from "react";
import { InviteCard } from "./app-ui/invite-card";

import { toast } from "sonner";

import acceptClassInvite from "@/actions/instructor-page/membership-management/accept-class-invite";
import declineClassInvite from "@/actions/instructor-page/membership-management/decline-class-invite";
import { formatDistanceToNow } from "date-fns";
import { tr } from "date-fns/locale";
import { useTranslations } from "next-intl";

interface ClassInviteItemProps {
    invite: {
        id: number;
        classId: number;
        className: string;
        classDescription: string | null;
        inviterName: string;
        inviterEmail: string;
        createdAt: Date;
        expiresAt: Date;
    };
    onActionComplete: () => Promise<void>;
}

const ClassInviteItem = ({
    invite,
    onActionComplete,
}: ClassInviteItemProps) => {
    const t = useTranslations("classes");
    const [isPending, startTransition] = useTransition();

    const handleAccept = () => {
        startTransition(async () => {
            try {
                const result = await acceptClassInvite(invite.id);

                if (result.success) {
                    toast.success(t("successTitle"), {
                        description: result.message || t("joinedClass"),
                    });
                    await onActionComplete();
                } else {
                    toast.error("Hata!", {
                        description:
                            result.error?.message || "Davet kabul edilemedi",
                    });
                }
            } catch (error) {
                toast.error("Hata!", {
                    description: t("unexpectedError"),
                });
            }
        });
    };

    const handleDecline = () => {
        startTransition(async () => {
            try {
                const result = await declineClassInvite(invite.id);

                if (result.success) {
                    toast.success(t("successTitle"), {
                        description: result.message || "Davet reddedildi",
                    });
                    await onActionComplete();
                } else {
                    toast.error("Hata!", {
                        description:
                            result.error?.message || "Davet reddedilemedi",
                    });
                }
            } catch (error) {
                toast.error("Hata!", {
                    description: t("unexpectedError"),
                });
            }
        });
    };

    return (
        <InviteCard
            from={{
                nickname: invite.inviterName,
            }}
            title={
                <>
                    {t.rich("invitedYouTo", {
                        className: invite.className,
                        b: (chunks) => <strong>{chunks}</strong>,
                    })}
                </>
            }
            subtitle={invite.classDescription || undefined}
            metadata={
                <span className="text-gray-500">
                    {formatDistanceToNow(new Date(invite.createdAt), {
                        addSuffix: true,
                        locale: tr,
                    })}
                </span>
            }
            onAccept={handleAccept}
            onDecline={handleDecline}
            isLoading={isPending}
        />
    );
};

export default ClassInviteItem;
